import React, { useState, useMemo } from 'react'
import { Copy, Check } from 'lucide-react'
import type { ScriptConfig } from '../../../types/widget'
import { useAmplitude } from '../../../hooks/useAmplitude'
import { AmplitudeEvents } from '../../../types/amplitude'
import type { CopyCodeClickedProperties } from '../../../types/amplitude'

interface ScriptGeneratorProps {
  config: ScriptConfig
}

const ScriptGenerator: React.FC<ScriptGeneratorProps> = ({ config: _config }) => {
  const [copied, setCopied] = useState(false)
  const { track } = useAmplitude()

  const scriptContent = useMemo(() => {
    // Note: config is used for dependency tracking but script is currently static
    // Future: Use config to generate dynamic script with configuration
    return `<script src="https://widget-deploy-hazel.vercel.app/widget.js" defer></script>`
  }, [])

  const handleCopy = async () => {
    // Track the event
    const eventProperties: CopyCodeClickedProperties = {
      url: window.location.href,
      description: 'simple-landing-page-test',
      timestamp: new Date().toISOString(),
    }
    track(AmplitudeEvents.COPY_CODE_CLICKED, eventProperties)

    try {
      await navigator.clipboard.writeText(scriptContent)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <div className="script-generator">
      <div className="script-generator-header">
        <label className="script-generator-label">Installation Script</label>
        <p className="script-generator-description">
          Copy this code and paste it into your website's HTML, just before the closing
          &lt;/body&gt; tag
        </p>
      </div>

      <div className="script-container">
        <pre className="script-content">
          <code>{scriptContent}</code>
        </pre>
        <button
          type="button"
          className="script-copy-button"
          onClick={handleCopy}
          aria-label={copied ? 'Copied!' : 'Copy to clipboard'}
        >
          {copied ? (
            <>
              <Check size={16} />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy size={16} />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>

      <div className="script-instructions">
        <h4>Quick Start:</h4>
        <ol>
          <li>Copy the script above</li>
          <li>Open your website's HTML file or template</li>
          <li>Paste the script just before the closing &lt;/body&gt; tag</li>
          <li>Save and deploy your changes</li>
        </ol>
      </div>
    </div>
  )
}

export default ScriptGenerator
